/**
 * Card Grid — layout system and card type catalogue for the learner home.
 *
 * Each card type entry documents:
 *   - Default, min, max column span
 *   - Height behaviour (natural vs stretch)
 *   - Position / ordering rules
 *   - Trigger condition
 *   - Live demo in a 6-column grid context
 */
import { type ReactNode } from 'react';
import { Section } from '../shared';
import { HeroCard, ProgressRing, Button, Surface, GridList } from '@/interfaces/learner/ui';

// ─── Inline card components (design system preview only) ─────────────────────

type CardStatus = 'new' | 'recent' | 'due';

const STATUS_LABEL: Record<CardStatus, string> = {
  new: 'Not yet studied',
  recent: 'Recently studied',
  due: 'Due for revision',
};

const STATUS_STYLE: Record<CardStatus, React.CSSProperties> = {
  new: { color: 'var(--color-warning-text)', background: 'var(--color-warning-subtle)' },
  recent: { color: 'var(--color-text-muted)', background: 'var(--color-surface-muted)' },
  due: { color: 'var(--color-error-text)', background: 'var(--color-error-subtle)' },
};

const STEP_DOTS_MAX = 9;

function PersonalLessonCard({
  lessonTitle,
  steps,
  created,
  folder,
  accentColor,
  status,
  onClick,
}: {
  lessonTitle: string;
  steps: number;
  created: string;
  folder?: string;
  accentColor: string;
  status?: CardStatus;
  onClick?: () => void;
}) {
  const dotCount = Math.min(steps, STEP_DOTS_MAX);
  const hasMore = steps > STEP_DOTS_MAX;
  return (
    <Surface
      interactive
      onClick={onClick}
      padding={0}
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <div style={{ height: 3, background: accentColor, flexShrink: 0 }} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, minHeight: 18 }}
        >
          {folder && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: 'var(--color-text-muted)',
                background: 'var(--color-surface-muted)',
                padding: '1px 7px',
                borderRadius: 9999,
              }}
            >
              {folder}
            </span>
          )}
          {status && status !== 'recent' && (
            <span
              style={{
                marginLeft: folder ? 'auto' : 0,
                fontSize: 10,
                fontWeight: 500,
                padding: '1px 7px',
                borderRadius: 9999,
                ...STATUS_STYLE[status],
              }}
            >
              {STATUS_LABEL[status]}
            </span>
          )}
        </div>
        <h4
          style={{
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'var(--color-text)',
            lineHeight: 1.35,
            marginBottom: 10,
          }}
        >
          {lessonTitle}
        </h4>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {Array.from({ length: dotCount }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 9999,
                  background: accentColor,
                  opacity: 0.5 + (i / dotCount) * 0.5,
                }}
              />
            ))}
            {hasMore && (
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 2 }}>
                +{steps - STEP_DOTS_MAX}
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{created}</span>
        </div>
      </div>
    </Surface>
  );
}

function RevisionTopicCard({
  topic,
  accuracy,
  questionCount,
  onClick,
}: {
  topic: string;
  accuracy: number;
  questionCount: number;
  onClick?: () => void;
}) {
  const color =
    accuracy < 50
      ? 'var(--color-error)'
      : accuracy < 65
        ? 'var(--color-warning)'
        : 'var(--color-success)';
  return (
    <Surface interactive onClick={onClick} padding={16}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
            marginBottom: 6,
          }}
        >
          {topic}
        </span>
        <div
          style={{
            fontSize: 40,
            fontWeight: 800,
            color,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            fontFamily: 'var(--font-display)',
            marginBottom: 12,
          }}
        >
          {accuracy}%
        </div>
        <div
          style={{
            height: 3,
            borderRadius: 9999,
            background: 'var(--color-surface-track)',
            overflow: 'hidden',
            marginBottom: 8,
          }}
        >
          <div
            style={{
              width: `${accuracy}%`,
              height: '100%',
              borderRadius: 9999,
              background: color,
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {questionCount} questions
        </span>
      </div>
    </Surface>
  );
}
import { PromotionalBanner } from '../../home/cards';
import { ArrowRight, Play } from 'lucide-react';

// ─── Color constants ──────────────────────────────────────────────────────────

const ACCENT = 'var(--color-accent)';
const INFO = 'var(--color-info)';
const WARNING = 'var(--color-warning)';
const MUTED = '#9ca3af';

// ─── Shared primitives ────────────────────────────────────────────────────────

const SMALL_LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
};

function SizeSwatch({
  label,
  description,
  span,
  color,
  tag,
}: {
  label: string;
  description: string;
  span: string;
  color: string;
  tag?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          height: 56,
          borderRadius: 10,
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
          {label}
        </span>
        {tag && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              padding: '2px 7px',
              borderRadius: 9999,
            }}
          >
            {tag}
          </span>
        )}
      </div>
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 2 }}>
          {span}
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
          {description}
        </p>
      </div>
    </div>
  );
}

function RowDiagram({
  label,
  cells,
}: {
  label: string;
  cells: { span: number; name: string; color: string }[];
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--color-text-muted)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
        {cells.map((cell, i) => (
          <div
            key={i}
            style={{
              gridColumn: `span ${cell.span}`,
              height: 36,
              borderRadius: 8,
              background: cell.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>{cell.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Rule({ title, body }: { title: string; body: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 14,
        padding: '14px 16px',
        borderRadius: 10,
        border: '1px solid var(--color-surface-border)',
        background: 'var(--color-surface)',
      }}
    >
      <div
        style={{
          width: 6,
          borderRadius: 9999,
          background: ACCENT,
          flexShrink: 0,
          alignSelf: 'stretch',
        }}
      />
      <div>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 3 }}>
          {title}
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>{body}</p>
      </div>
    </div>
  );
}

// ─── Card catalogue components ─────────────────────────────────────────────────

/** Dashed placeholder in demo grids, representing "some other card". */
function Placeholder({ span }: { span: number }) {
  return (
    <div
      style={{
        gridColumn: `span ${span}`,
        minHeight: 72,
        borderRadius: 12,
        border: '1.5px dashed var(--color-surface-border)',
        background: 'var(--color-surface-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <span style={{ ...SMALL_LABEL }}>other card</span>
    </div>
  );
}

/** Spec row rendered below the card type header. */
function SpecRow({ items }: { items: [string, string][] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        borderBottom: '1px solid var(--color-surface-border)',
      }}
    >
      {items.map(([label, value], i) => (
        <div
          key={i}
          style={{
            padding: '10px 14px',
            borderLeft: i > 0 ? '1px solid var(--color-surface-border)' : 'none',
            background: 'var(--color-surface)',
          }}
        >
          <p style={{ ...SMALL_LABEL, marginBottom: 3 }}>{label}</p>
          <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text)', lineHeight: 1.4 }}>
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

const SIZE_COLOR: Record<string, string> = {
  sm: WARNING,
  md: INFO,
  lg: ACCENT,
  banner: MUTED,
};

/** Container wrapping spec + trigger + demo for one card type. */
function CardTypeEntry({
  name,
  size,
  component,
  trigger,
  specItems,
  demo,
}: {
  name: string;
  size: 'sm' | 'md' | 'lg' | 'banner';
  component: string;
  trigger: string;
  specItems: [string, string][];
  demo: ReactNode;
}) {
  return (
    <div
      style={{
        borderRadius: 12,
        border: '1px solid var(--color-surface-border)',
        overflow: 'hidden',
        marginBottom: 20,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: 'var(--color-surface-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: 'var(--color-text)',
            letterSpacing: '-0.01em',
            flex: 1,
          }}
        >
          {name}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#fff',
            background: SIZE_COLOR[size],
            padding: '3px 9px',
            borderRadius: 9999,
          }}
        >
          {size}
        </span>
        <code
          style={{
            fontSize: 11,
            color: 'var(--color-text-muted)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-surface-border)',
            padding: '2px 8px',
            borderRadius: 6,
          }}
        >
          {component}
        </code>
      </div>

      {/* Spec grid */}
      <SpecRow items={specItems} />

      {/* Trigger */}
      <div
        style={{
          padding: '8px 16px',
          background: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-surface-border)',
          display: 'flex',
          gap: 6,
          alignItems: 'baseline',
        }}
      >
        <span style={{ ...SMALL_LABEL }}>Trigger</span>
        <span style={{ fontSize: 12, color: 'var(--color-text)' }}>{trigger}</span>
      </div>

      {/* Live demo */}
      <div style={{ padding: 16, background: 'var(--color-bg)' }}>
        <p style={{ ...SMALL_LABEL, marginBottom: 10 }}>In grid context</p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 10,
            alignItems: 'start',
          }}
        >
          {demo}
        </div>
      </div>
    </div>
  );
}

// ─── Inline stubs for home-specific cards ─────────────────────────────────────
// These are demo-only versions used in this style guide.
// The real implementations live in LearnerHome.tsx until extracted.

function MyLessonsDemo() {
  return (
    <Surface interactive padding={16} style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ ...SMALL_LABEL, marginBottom: 6 }}>My Lessons</span>
      <div
        style={{
          fontSize: 40,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          lineHeight: 1,
          color: 'var(--color-accent)',
          fontFamily: 'var(--font-display)',
          marginBottom: 'auto',
        }}
      >
        12
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--color-accent-text)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginTop: 12,
        }}
      >
        View all <ArrowRight size={11} />
      </span>
    </Surface>
  );
}

function StreakDemo() {
  return (
    <Surface interactive padding={16} style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={{ ...SMALL_LABEL, marginBottom: 6 }}>Streak</span>
      <div
        style={{
          fontSize: 40,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          lineHeight: 1,
          color: 'var(--color-warning)',
          fontFamily: 'var(--font-display)',
          flex: 1,
          marginBottom: 6,
        }}
      >
        7
      </div>
      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>days in a row</span>
    </Surface>
  );
}

const WEEK_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const STUDIED_DAYS = [true, true, true, false, false, false, false];

function WeeklyGoalDemo() {
  return (
    <Surface padding={24} style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
      <div style={{ flexShrink: 0 }}>
        <span style={SMALL_LABEL}>This week</span>
        <div
          style={{
            fontSize: 40,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: 'var(--color-accent)',
            fontFamily: 'var(--font-display)',
            marginTop: 6,
            marginBottom: 4,
          }}
        >
          5
        </div>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>of 10 lessons</span>
      </div>
      <div
        style={{
          width: 1,
          alignSelf: 'stretch',
          background: 'var(--color-surface-border)',
          flexShrink: 0,
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        {WEEK_LABELS.map((label, i) => (
          <div
            key={i}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}
          >
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: STUDIED_DAYS[i] ? 'var(--color-accent)' : 'var(--color-surface-track)',
                border: STUDIED_DAYS[i] ? 'none' : '1px solid var(--color-surface-border)',
              }}
            />
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                letterSpacing: '0.04em',
              }}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    </Surface>
  );
}

const DISCOVER_ITEMS = [
  {
    id: '1',
    leading: 'ML',
    leadingColor: 'var(--color-accent)',
    title: 'Intro to Machine Learning',
    subtitle: '24 lessons · Beginner',
  },
  {
    id: '2',
    leading: 'PY',
    leadingColor: 'var(--color-info)',
    title: 'Python for Data Science',
    subtitle: '18 lessons · Intermediate',
  },
];

function DiscoverDemo() {
  return (
    <Surface padding={0} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--color-surface-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={SMALL_LABEL}>Discover</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--color-accent-text)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          Browse all <ArrowRight size={11} />
        </span>
      </div>
      <GridList
        surface={false}
        items={DISCOVER_ITEMS}
        onAction={() => {
          /* demo */
        }}
      />
    </Surface>
  );
}

function GradientBannerDemo() {
  return (
    <PromotionalBanner
      title="Unlock unlimited lessons"
      description="Generate as many personal lessons as you want with Formency Premium."
      ctaLabel="Learn more"
    />
  );
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const COURSE_NAV_ITEMS = [
  {
    id: '1',
    leading: 'QM',
    leadingColor: 'hsl(270,50%,55%)',
    title: 'Quantum Mechanics',
    subtitle: '8 of 24 lessons',
    trailing: '33%',
  },
  {
    id: '2',
    leading: 'WJ',
    leadingColor: 'hsl(30,50%,55%)',
    title: 'Woodworking Joinery',
    subtitle: 'Complete',
  },
  {
    id: '3',
    leading: 'CS',
    leadingColor: 'hsl(160,50%,55%)',
    title: 'Conversational Spanish',
    subtitle: '3 of 32 lessons',
    trailing: '9%',
  },
];

// ─── Tab ─────────────────────────────────────────────────────────────────────

export default function CardGridTab() {
  return (
    <>
      {/* Size tokens */}
      <Section label="Card Sizes">
        <p
          style={{
            fontSize: 13,
            color: 'var(--color-text-muted)',
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          All cards live in a 6-column grid. Each card type has a fixed size — it does not shrink or
          grow based on available space. Valid row sums are always exactly 6.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <SizeSwatch
            label="sm"
            description="Compact data cards. Max 1/3 width. Revision topics, streak, my lessons."
            span="span 2 of 6"
            color={WARNING}
          />
          <SizeSwatch
            label="md"
            description="Standard feature cards. Half width. Discover, future contextual cards."
            span="span 3 of 6"
            color={INFO}
          />
          <SizeSwatch
            label="lg"
            description="Prominent feature cards. Two-thirds width. Hero, course nav, weekly goal."
            span="span 4 of 6"
            color={ACCENT}
          />
          <SizeSwatch
            label="banner"
            description="Full width. Announcements and upsells only — never for content cards."
            span="span 6 of 6"
            color={MUTED}
            tag="restricted"
          />
        </div>
      </Section>

      {/* Valid row compositions */}
      <Section label="Valid Row Compositions">
        <p
          style={{
            fontSize: 13,
            color: 'var(--color-text-muted)',
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          Spans per row must sum to exactly 6. The card registry is responsible for ordering cards
          to produce complete rows. These are the only valid combinations.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <RowDiagram
            label="lg + sm (4+2)"
            cells={[
              { span: 4, name: 'lg', color: ACCENT },
              { span: 2, name: 'sm', color: WARNING },
            ]}
          />
          <RowDiagram
            label="sm + lg (2+4) — order can vary"
            cells={[
              { span: 2, name: 'sm', color: WARNING },
              { span: 4, name: 'lg', color: ACCENT },
            ]}
          />
          <RowDiagram
            label="md + md (3+3)"
            cells={[
              { span: 3, name: 'md', color: INFO },
              { span: 3, name: 'md', color: INFO },
            ]}
          />
          <RowDiagram
            label="sm + sm + sm (2+2+2)"
            cells={[
              { span: 2, name: 'sm', color: WARNING },
              { span: 2, name: 'sm', color: WARNING },
              { span: 2, name: 'sm', color: WARNING },
            ]}
          />
          <RowDiagram
            label="banner (6) — announcements only"
            cells={[{ span: 6, name: 'banner', color: MUTED }]}
          />
        </div>
      </Section>

      {/* Layout rules */}
      <Section label="Layout Rules">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Rule
            title="No orphaned whitespace"
            body="Every row must sum to exactly 6. The card registry must produce an ordered list where spans add up per row. If they cannot (e.g. only one sm exists), the card registry promotes the card to fill the row."
          />
          <Rule
            title="No full-width content cards"
            body="Only banner cards (announcements, upsells) may span all 6 columns. Hero, course nav, weekly goal, personal lessons — never full width."
          />
          <Rule
            title="sm cards are never promoted"
            body="RevisionTopicCard, StreakCard, MyLessonsCard, and PersonalLessonCard are always span-2 (sm). They never grow to md or lg regardless of context."
          />
          <Rule
            title="Hero is always first"
            body="The Hero/Resume card occupies Row 1, Position 1. It is never reordered. All other cards flow beneath it."
          />
          <Rule
            title="lg cards stretch, sm cards don't"
            body="lg cards get align-self: stretch so they fill the full row height. sm cards sit at natural height (align-self: start), anchored to the top of the row."
          />
          <Rule
            title="Status badges are the only urgency signal"
            body="Cards communicate state via the status badge only (amber 'Not yet studied', red 'Due for revision'). Never change background, border, or layout to signal urgency."
          />
        </div>
      </Section>

      {/* Card type catalogue */}
      <Section label="Card Type Catalogue">
        <p
          style={{
            fontSize: 13,
            color: 'var(--color-text-muted)',
            marginBottom: 24,
            lineHeight: 1.6,
          }}
        >
          Every card type that can appear on the learner home. Each entry defines the column rules,
          height behaviour, position constraints, and the trigger that causes it to appear.
        </p>

        {/* 1. Hero / Resume */}
        <CardTypeEntry
          name="Hero / Resume"
          size="lg"
          component="HeroCard"
          trigger="User has at least one active course enrolment"
          specItems={[
            ['Default', 'span 4 (lg)'],
            ['Min', 'span 4 — fixed'],
            ['Max', 'span 4 — fixed'],
            ['Height', 'Stretches to match companion sm'],
            ['Position', 'Row 1 · Position 1 · Always first'],
          ]}
          demo={
            <>
              <div
                style={{
                  gridColumn: 'span 4',
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                }}
              >
                <HeroCard
                  leading={<ProgressRing value={42} />}
                  eyebrow="Course · 12 lessons"
                  eyebrowColor="var(--color-accent)"
                  title="Introduction to Algorithms"
                  subtitle="Lesson 5 of 12 · ~2.4h remaining"
                  actions={
                    <Button variant="primary" icon={<Play size={15} strokeWidth={2.5} />}>
                      Resume
                    </Button>
                  }
                  style={{ flex: 1 }}
                />
              </div>
              <Placeholder span={2} />
            </>
          }
        />

        {/* 2. Course Nav List */}
        <CardTypeEntry
          name="Course Nav List"
          size="lg"
          component="GridList"
          trigger="User is enrolled in at least one course"
          specItems={[
            ['Default', 'span 4 (lg)'],
            ['Min', 'span 4 — fixed'],
            ['Max', 'span 4 — fixed'],
            ['Height', 'Stretches to match companion sm'],
            ['Position', 'After Hero row · before motivational cards'],
          ]}
          demo={
            <>
              <div
                style={{
                  gridColumn: 'span 4',
                  alignSelf: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <GridList
                  items={COURSE_NAV_ITEMS}
                  onAction={() => {
                    /* demo */
                  }}
                  style={{ flex: 1 }}
                />
              </div>
              <Placeholder span={2} />
            </>
          }
        />

        {/* 3. Personal Lesson — new/unstudied */}
        <CardTypeEntry
          name="Personal Lesson (new / not yet studied)"
          size="sm"
          component="PersonalLessonCard"
          trigger="User has a personal lesson that has never been opened"
          specItems={[
            ['Default', 'span 2 (sm)'],
            ['Min', 'span 2 — fixed'],
            ['Max', 'span 2 — never promoted'],
            ['Height', 'Natural (align-self: start)'],
            ['Position', 'Pairs with lg card in same row · status="new"'],
          ]}
          demo={
            <>
              <Placeholder span={4} />
              <div style={{ gridColumn: 'span 2' }}>
                <PersonalLessonCard
                  lessonTitle="Introduction to Neural Networks"
                  steps={6}
                  created="2 days ago"
                  folder="Data Science"
                  accentColor="var(--color-accent)"
                  status="new"
                />
              </div>
            </>
          }
        />

        {/* 4. Personal Lesson — due for revision */}
        <CardTypeEntry
          name="Personal Lesson (due for revision)"
          size="sm"
          component="PersonalLessonCard"
          trigger="User has a personal lesson due for spaced-repetition review"
          specItems={[
            ['Default', 'span 2 (sm)'],
            ['Min', 'span 2 — fixed'],
            ['Max', 'span 2 — never promoted'],
            ['Height', 'Natural (align-self: start)'],
            ['Position', 'Pairs with lg card · status="due" — shown above studied lessons'],
          ]}
          demo={
            <>
              <Placeholder span={4} />
              <div style={{ gridColumn: 'span 2' }}>
                <PersonalLessonCard
                  lessonTitle="Sorting Algorithms"
                  steps={8}
                  created="1 week ago"
                  folder="CS Basics"
                  accentColor="var(--color-error)"
                  status="due"
                />
              </div>
            </>
          }
        />

        {/* 5. Revision Topic */}
        <CardTypeEntry
          name="Revision Topic"
          size="sm"
          component="RevisionTopicCard"
          trigger="A topic's accuracy score drops below 65% — max 3 shown at a time"
          specItems={[
            ['Default', 'span 2 (sm)'],
            ['Min', 'span 2 — fixed'],
            ['Max', 'span 2 — NEVER promoted'],
            ['Height', 'Natural (align-self: start) · uniform across row'],
            ['Position', 'Groups of 3 fill one sm+sm+sm row · after personal lesson row'],
          ]}
          demo={
            <>
              <div style={{ gridColumn: 'span 2' }}>
                <RevisionTopicCard topic="Cell Biology" accuracy={40} questionCount={23} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <RevisionTopicCard topic="Energy Balance" accuracy={45} questionCount={17} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <RevisionTopicCard topic="Photosynthesis" accuracy={58} questionCount={31} />
              </div>
            </>
          }
        />

        {/* 6. My Lessons */}
        <CardTypeEntry
          name="My Lessons"
          size="sm"
          component="MyLessonsCard (LearnerHome)"
          trigger="User has at least one personal lesson"
          specItems={[
            ['Default', 'span 2 (sm)'],
            ['Min', 'span 2 — fixed'],
            ['Max', 'span 2 — fixed'],
            ['Height', 'Natural (align-self: start)'],
            ['Position', 'Pairs with Course Nav List in same row'],
          ]}
          demo={
            <>
              <Placeholder span={4} />
              <div style={{ gridColumn: 'span 2' }}>
                <MyLessonsDemo />
              </div>
            </>
          }
        />

        {/* 7. Streak */}
        <CardTypeEntry
          name="Streak"
          size="sm"
          component="StreakCard (LearnerHome)"
          trigger="User has an active streak of 2 or more days"
          specItems={[
            ['Default', 'span 2 (sm)'],
            ['Min', 'span 2 — fixed'],
            ['Max', 'span 2 — fixed'],
            ['Height', 'Natural (align-self: start)'],
            ['Position', 'Pairs with Weekly Goal card in same row'],
          ]}
          demo={
            <>
              <Placeholder span={4} />
              <div style={{ gridColumn: 'span 2' }}>
                <StreakDemo />
              </div>
            </>
          }
        />

        {/* 8. Weekly Goal */}
        <CardTypeEntry
          name="Weekly Goal"
          size="lg"
          component="WeeklyGoalCard (LearnerHome)"
          trigger="Always shown when authenticated — provides persistent weekly context"
          specItems={[
            ['Default', 'span 4 (lg)'],
            ['Min', 'span 4 — fixed'],
            ['Max', 'span 4 — fixed'],
            ['Height', 'Stretches to match Streak card'],
            ['Position', 'Motivational row · after Course Nav row'],
          ]}
          demo={
            <>
              <div
                style={{
                  gridColumn: 'span 4',
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                }}
              >
                <WeeklyGoalDemo />
              </div>
              <Placeholder span={2} />
            </>
          }
        />

        {/* 9. Discover */}
        <CardTypeEntry
          name="Discover"
          size="md"
          component="DiscoverCard (not yet built)"
          trigger="User has fewer than 2 enrolled courses — surfaces recommended content"
          specItems={[
            ['Default', 'span 3 (md)'],
            ['Min', 'span 3 — fixed'],
            ['Max', 'span 3 — fixed'],
            ['Height', 'Natural (align-self: start)'],
            ['Position', 'Pairs with another md card · near bottom of page'],
          ]}
          demo={
            <>
              <div style={{ gridColumn: 'span 3' }}>
                <DiscoverDemo />
              </div>
              <Placeholder span={3} />
            </>
          }
        />

        {/* 10. Gradient Banner */}
        <CardTypeEntry
          name="Gradient Banner"
          size="banner"
          component="GradientBanner (LearnerHome)"
          trigger="Dismissable upsell/announcement — reappears on refresh"
          specItems={[
            ['Default', 'span 6 (banner)'],
            ['Min', 'span 6 — fixed'],
            ['Max', 'span 6 — fixed'],
            ['Height', 'Natural — content-driven'],
            ['Position', 'Row 0 · above all content cards'],
          ]}
          demo={
            <div style={{ gridColumn: 'span 6' }}>
              <GradientBannerDemo />
            </div>
          }
        />
      </Section>
    </>
  );
}
